import type { BlockStateUnionType, BlockType } from '@unifyapps/defs/types/block';
import type { JavaScriptFunction } from '@unifyapps/defs/types/javascriptFunction';
import { DependencyHelper } from '../../../dependency/helpers/DependencyHelper';
import { PageFunctionHelper } from '../../../helpers/PageFunctionHelper';
import noCodeBuildDependencyGraph from '../../dependency/noCodeBuildDependencyGraph';
import DataSourceHelper from '../../helper/DataSourceHelper';
import type { UnsavedDataSource } from '../../stores/DataSourceRecordStore';
import { isNoCodeEntityId } from '../../utils/isNoCodeEntityId';
import {
  dedupeDynamicAndConditionalPathsHandler,
  dynamicAndConditionalPathsHandler,
} from './handlers';
import type { EntityListType } from './types';

export const noCodeBuildDependencyGraphParams = {
  isValidEntity: isNoCodeEntityId,
  handlers: [dynamicAndConditionalPathsHandler],
  dedupeHandlers: [dedupeDynamicAndConditionalPathsHandler],
};

// no need to remove dependsOn etc. here because we don't pick them in block state
function blockDependencyBuilder({
  blockState,
  block,
}: {
  blockState: NonNullable<BlockStateUnionType>;
  block: BlockType;
}) {
  const blockDependencies = noCodeBuildDependencyGraph({
    objects: {
      [blockState.id]: {
        // dynamic content and condition operators present inside the block are not added in dynPaths and condPaths,
        // so any {{ }} will be added in dependsOn but not in dyn and cond paths
        ...blockState,
      },
    },
    ...noCodeBuildDependencyGraphParams,
  });

  const blockDependsOn = DependencyHelper.getDependsOn(blockDependencies[blockState.id]) ?? [];

  return {
    [blockState.id]: {
      ...blockDependencies[blockState.id],
      dependsOn: blockDependsOn,
    },
  };
}

function pageFunctionDependencyBuilder({ pageFunction }: { pageFunction: JavaScriptFunction }) {
  const adaptedPageFunction =
    PageFunctionHelper.getAdaptedPageFunctionForDependencyGraph(pageFunction);
  return noCodeBuildDependencyGraph({
    objects: { [pageFunction.id]: adaptedPageFunction },
    ...noCodeBuildDependencyGraphParams,
  });
}

function dataSourceDependencyBuilder({ dataSource }: { dataSource: UnsavedDataSource }) {
  const { adaptedDataSources } = DataSourceHelper.getAdaptedDataSourcesForDependencyGraph({
    [dataSource.id]: dataSource,
  });

  return noCodeBuildDependencyGraph({
    objects: { [dataSource.id]: adaptedDataSources[dataSource.id] },
    ...noCodeBuildDependencyGraphParams,
  });
}

function pageFieldDependencyBuilder({
  fieldKey,
  fieldValue,
}: {
  fieldKey: keyof Pick<EntityListType, 'documentTitle'>;
  fieldValue: unknown;
}) {
  return noCodeBuildDependencyGraph({
    objects: { [fieldKey]: fieldValue },
    ...noCodeBuildDependencyGraphParams,
  });
}

export {
  blockDependencyBuilder,
  pageFunctionDependencyBuilder,
  dataSourceDependencyBuilder,
  pageFieldDependencyBuilder,
};
